/**
 * Document Capture types (86d3rrf3e) — mirror the CSC_CORE DTOs behind
 * `GET/POST /api/document-capture/*`. Server dates arrive as ISO strings
 * (C# `DateOnly` → `"2026-07-20"`, `DateTime` → ISO 8601 Z).
 *
 * Enums are transported as INTEGERS (the API serializes them as ints, like the rest of DOP),
 * so these are numeric enums whose values must match CSC_CORE exactly — a drift here shows up
 * as a silently wrong chip, not a type error.
 */

// ---------------------------------------------------------------- enums (values match CSC_CORE)

/** Scan lifecycle. Independent of {@link DocLinkStatus} — a document can be Captured but unlinked. */
export enum DocScanStatus {
  Captured = 0,
  NeedsReview = 1,
  Resolved = 2,
  Rejected = 3,
}

/** NetSuite link lifecycle. Independent of {@link DocScanStatus}. */
export enum DocLinkStatus {
  /** Queued, not attempted yet. */
  Pending = 0,
  /** Not found in NetSuite yet — a brief transient, NOT an error. */
  AwaitingNs = 1,
  Linked = 2,
  /** Linked, but the NetSuite transaction is voided. */
  LinkedVoid = 3,
  /** The accounting period is closed, so the field could not be written. */
  SkippedClosedPeriod = 4,
  /** The custbody field already held a value we didn't set — a human decides. */
  Conflict = 5,
  Failed = 6,
  /** Was linked; the link was deliberately cleared (reject & unlink). */
  LinkBroken = 7,
  /**
   * 86d45ffw4 - a reviewer says they pasted the link onto the NetSuite record by hand, because DOP
   * could not. This state means UNCONFIRMED by construction: the reconciliation sweep moves it to
   * {@link Linked} the moment it actually sees this document's token on the record, so a row still
   * sitting here is one nobody has verified. That is why its chip is warning-toned, not success.
   */
  ManuallyLinked = 8,
  /**
   * 14ykpqtwauu - NOT linked on purpose: the record is a journal whose credit lines are applied, and
   * NetSuite un-applies them whenever a journal is saved (our link write was a save).
   * RETIRED 14ykpqtx1z9 (2026-10-26): journal links go through NetSuite's link service now, an inline
   * edit that leaves applications alone. Nothing sets this; the retirement sweep drains it. The value
   * stays for the rows that carry it and is never renumbered.
   */
  LinkWithheld = 9,
}

export enum DocReviewKind {
  Scan = 0,
  NetSuite = 1,
  Replacement = 2,
}

export enum DocReviewReason {
  NoBarcode = 0,
  MultipleNumbers = 1,
  NumberNotOnPage1 = 2,
  NonConsecutiveRepeat = 3,
  NeverAppearedInNs = 4,
  MultipleNsMatches = 5,
  WrongRecordFamily = 6,
  NsVoided = 7,
  CustbodyConflict = 8,
  ClosedPeriod = 9,
  LinkFailed = 10,
  FewerPages = 11,
  SupersedePending = 12,
  LinkBroken = 13,
  UnknownPrefix = 14,

  // ---- 86d3vbwup W3/W9 — decode-terminal + held-replacement arrivals ----

  /** The file couldn't be read after all attempts. Terminal: nothing promoted, current version untouched. */
  DecodeFailed = 15,
  /** A deliberate replace whose barcode reads a DIFFERENT document — held, never auto-promoted. */
  HeldMismatch = 16,
  /** A deliberate replace reading more than one document number — held. */
  HeldMultiple = 17,
  /** A deliberate replace with no readable barcode to verify against — held. */
  HeldNoBarcode = 18,

  // ---- 86d3x48q2/86d3x48q7 (T12/T13) — AI second reader outcomes ----
  //
  // ⚠️ These travel as INTEGERS and must match CSC_CORE's DocReviewReasonEnum exactly. Drift shows
  // up as a silently wrong chip on a real review item, never as a type error.

  /** Both readers produced a number and the numbers differ. Neither is assumed right — a person decides. */
  IdentificationConflict = 19,
  /** The barcode read nothing; the AI read a number off the page. Expected the LARGEST group. */
  AiSuggestion = 20,
  /** The barcode read a number but the AI declined to read one, so nothing corroborates it. */
  Unconfirmed = 21,
  /** The AI call itself failed after retries — there was no second reading at all. Distinct from
   *  "the AI read nothing": a different problem needing a different fix. */
  VerifierUnavailable = 22,
  /** Spot check: both readers agreed and the scan ALREADY linked with nobody looking. Sampled back
   *  to a person so agreement accuracy is a measured number rather than an assumption. */
  AutoLinkAudit = 23,
  /** NetSuite refused the VALUE we sent and the queue has STOPPED. Distinct from LinkFailed,
   *  which is still retrying — the reviewer is told different things. */
  LinkRefused = 24,
  /** 14ykpqtwauu - a journal whose credits are applied; linking it would un-apply them. `reasonValue`
   *  is `"{lines}|{amount}"`, or null when DOP could not check. RETIRED 14ykpqtx1z9: nothing raises it
   *  any more; resolved items keep the value, so it stays and is never renumbered. */
  AppliedCredits = 25,
}

export enum DocReviewState {
  Open = 0,
  Resolved = 1,
  SelfResolved = 2,
  Superseded = 3,
}

export enum DocSupersedeState {
  Active = 0,
  PendingApproval = 1,
  Historical = 2,
}

export enum DocFileProcessState {
  Queued = 0,
  Decoding = 1,
  Decoded = 2,
  DecodeFailed = 3,
  Quarantined = 4,
}

export enum DocFileSource {
  Watcher = 0,
  Manual = 1,
  Backfill = 2,
  ReviewAssigned = 3,
  /** An earlier version copied forward as the new current version (T7) — shares its source's bytes. */
  Restore = 4,
}

// ---------------------------------------------------------------- registry + viewer

/** One row of the registry. Flat by design — the list must not need a second request per row. */
export interface DocumentListItem {
  id: number;
  docNumber: string;
  /** Series prefix: DR | INV | INT | CM | OP | DV. Drives family identity via the config map. */
  prefix: string;
  status: DocScanStatus;
  linkStatus: DocLinkStatus;
  /** When the document was first captured — the same field the Scanned filter matches. */
  scannedAt: string;
  /** NetSuite enrichment; null until the link lane resolves the record. */
  tranDate: string | null;
  entityName: string | null;
  amount: number | null;
  nsVoided: boolean;
  activeFileId: number | null;
  activeVersion: number | null;
  pageCount: number;
  versionCount: number;
  hasOpenReview: boolean;
  /**
   * 86d45ffw4 - when a reviewer confirmed they pasted the link into NetSuite themselves. Survives the
   * sweep's promotion to Linked, so a Linked row carrying this is one that took a human.
   */
  manualLinkedAt: string | null;
  /** Their display name. Null if the user record is gone - the chip then says only WHEN. */
  manualLinkedByName: string | null;
}

export interface DocumentVersion {
  fileId: number;
  version: number | null;
  supersedeState: DocSupersedeState;
  processState: DocFileProcessState;
  pageCount: number;
  sizeBytes: number;
  source: DocFileSource;
  stationId: string | null;
  uploadedById: number;
  uploadedAt: string;
  /** Optional per-version note (T7) — user-supplied on update, auto "Restored from v{n}" on restore. */
  note: string | null;
  /** Uploader display name for the version-history meta line (T7). */
  uploadedByName: string | null;
}

/**
 * 86d3vbwup W9 — display state of a version row, DERIVED from the two server enums (no new DB
 * state): Active/Historical map directly; a PendingApproval row is `checking` while its decode is
 * in flight, `held` once decoded but awaiting a reviewer, `failed` when unreadable. Everything
 * user-visible about "the file" (page counts, chips, compare captions) reads the ACTIVE version;
 * a provisional row is never served.
 */
export type VersionDisplayState = 'active' | 'history' | 'checking' | 'held' | 'failed';

export function versionDisplayState(v: DocumentVersion): VersionDisplayState {
  if (v.supersedeState === DocSupersedeState.Active) return 'active';
  if (v.supersedeState === DocSupersedeState.Historical) return 'history';
  // PendingApproval — the provisional lane.
  if (v.processState === DocFileProcessState.Decoded) return 'held';
  if (
    v.processState === DocFileProcessState.DecodeFailed ||
    v.processState === DocFileProcessState.Quarantined
  )
    return 'failed';
  return 'checking'; // Queued | Decoding
}

/** The document's single in-flight/held/failed provisional version, if any (at most one by invariant). */
export function provisionalVersion(versions: DocumentVersion[]): DocumentVersion | undefined {
  return versions.find((v) => v.supersedeState === DocSupersedeState.PendingApproval);
}

/** Full viewer payload. `linkEpoch` is the fencing token every review decision must echo back. */
export interface DocumentView extends DocumentListItem {
  linkEpoch: number;
  nsInternalId: string | null;
  nsRecordType: string | null;
  enrichedAt: string | null;
  /** Server-assembled "Open in NetSuite" deep-link (86d3rrfee); null until linked. */
  netSuiteUrl: string | null;
  previousCustbodyValue: string | null;
  /**
   * The document's active capability link (86d3uu104, T10) — the same URL NetSuite points at, and what
   * every Copy affordance emits. Carried on the payload so Copy stays SYNCHRONOUS: an await between
   * the click and `navigator.clipboard.writeText` loses the user-gesture context in Safari and the
   * copy silently fails. Null when the document has no link yet (not linked, or freshly revived).
   */
  shareUrl: string | null;
  revivedByRescan: boolean;
  awaitingNsRecycleCount: number;
  versions: DocumentVersion[];
  openReviews: ReviewQueueItem[];
  /**
   * 86d3x48q7 (T13) — how the ACTIVE version's number was arrived at (the third status axis).
   * Null when no identification attempt exists, i.e. every document captured before this feature —
   * the chip is then HIDDEN rather than shown as "Not checked", which would claim the AI call
   * failed. Scoped to the active version so it always describes the paper on screen.
   */
  verification: DocVerificationState | null;
}

// ---------------------------------------------------------------- review queue

export interface ReviewQueueItem {
  id: number;
  kind: DocReviewKind;
  reason: DocReviewReason;
  /** Plain-language sentence written when the item was raised — render VERBATIM, never re-word. */
  reasonDetail: string;
  /**
   * 86d3vbwup W9 — structured reason payload: for {@link DocReviewReason.HeldMismatch} this is the
   * document number the barcode actually reads (the re-file target). Null elsewhere.
   */
  reasonValue: string | null;
  state: DocReviewState;
  createdAt: string;
  fileId: number | null;
  documentId: number | null;
  docNumber: string | null;
  /**
   * Family this item belongs to: the document's series once attached, the decoder's best guess
   * while unattached, and null when genuinely unknown (treated as maximally sensitive).
   */
  effectivePrefix: string | null;
  /** Live document epoch to echo back on the decision. Null for items with no document yet. */
  linkEpoch: number | null;
  linkStatus: DocLinkStatus | null;
  pageCount: number;
  fileUploadedAt: string | null;
  stationId: string | null;
  tranDate: string | null;
  entityName: string | null;
  amount: number | null;
  previousCustbodyValue: string | null;
  /**
   * 86d45ffw4 - NetSuite's verbatim rejection body, shown folded away behind "What NetSuite sent
   * back". Null for every other reason AND for every item raised before this shipped (those still
   * carry the raw text in `reasonDetail`), so render the collapsible only when it is non-empty.
   */
  nsRawResponse: string | null;
  /**
   * 86d3x48q7 (T13) — the two readings and what corroborates each, for reasons 19–23.
   * Null for every review item raised outside the AI second reader (including all pre-feature items).
   *
   * <p>Why this is a structured block and not more `reasonValue`: a conflict needs SIX values — two
   * numbers, plus registry-existence and already-attached-elsewhere for each — and one nullable
   * string slot cannot hold six fields. Without them the conflict panel loses the two facts that
   * actually settle these cases.</p>
   *
   * <p>⚠️ These facts are DISPLAYED, NEVER GATED ON. Gating on an unvalidated signal is exactly how
   * the refuted quorum gate happened. They inform the person; they do not decide.</p>
   */
  identification: ReviewIdentification | null;
}

/**
 * Per-reader corroboration snapshot taken AT ATTEMPT TIME (86d3x48q2 T12).
 *
 * <p>Snapshotted rather than looked up live because resolution mutates claim state: once a reviewer
 * attaches the scan, "already attached to another scan" would answer differently than it did when
 * the readers disagreed, and the panel would quietly rewrite its own evidence.</p>
 *
 * <p>These two facts settle both real production failures on their own. Misfile A: the decoder's
 * `…626` exists NOWHERE in the registry. Misfile B: the decoder's `…628` is a REAL document
 * belonging to a different customer, already attached to another scan — right prefix, right record
 * type, exactly one match, wrong customer.</p>
 */
// ---------------------------------------------------------------- second reader (86d3x48q7 T13)

/**
 * Runtime settings for the AI second reader. Mirrors `DocScanVerifierConfigDto`.
 *
 * ⚠️ The operator picks a LEVEL, never a model. The four model names exist only for the
 * "show model names" disclosure — showing them in the operator's face invites tuning a thing whose
 * behaviour is only meaningful as a pair, and the level→model map is server-side anyway.
 */
export interface VerifierConfig {
  enabled: boolean;
  level: 'standard' | 'careful';
  spotCheckRatePercent: number;
  standardPrimaryModel: string;
  standardFallbackModel: string;
  carefulPrimaryModel: string;
  carefulFallbackModel: string;
  /**
   * 86d40jndv — READ-ONLY. The prompt lives in code and its version is derived from the prompt text
   * (`{label}+{sha256 prefix}`), so the server reports what is actually running rather than what a
   * config row happens to say. It is deliberately absent from the update payload: a field that
   * cannot change what runs would only invite someone to "bump the version" and believe it did
   * something.
   */
  promptVersion: string;
  dailyCallCap: number;
  /** 86d3yeh4j — tries per scan before it reaches a person (1–6, default 4). */
  maxVerificationAttempts: number;
  updatedAt: string;
  updatedBy: string | null;
}

/** Partial update — send only what changed. There is no row versioning anywhere in this codebase. */
export interface VerifierConfigUpdate {
  enabled?: boolean;
  level?: 'standard' | 'careful';
  spotCheckRatePercent?: number;
  maxVerificationAttempts?: number;
  /**
   * 86d3ygnz3 — the daily call cap. The server has accepted this field since T12 (validator 0–100,000)
   * but the client type never carried it, so the PUT could not express a cap change at all — which is
   * why production's three raises were all made by hand against the raw API.
   *
   * ⚠️ Include it ONLY when the admin actually edited the field. There is no row versioning anywhere
   * in this codebase, so an echoed-back untouched value is the one thing that would let a dialog
   * opened before an incident silently revert a colleague's mid-incident raise on an unrelated save.
   */
  dailyCallCap?: number;
}

/**
 * 86d3yeh4j — the second reader's circuit breaker, as the operator's banner reads it.
 *
 * ⚠️ NUMERIC, mirroring `DocVerifierBreakerState` in CSC_CORE. It was declared as a string union
 * (`'Closed' | 'Open' | 'HalfOpen'`) and the API has never sent strings: this API registers no
 * `JsonStringEnumConverter` — deliberately, see the note in `QuotationController` — so every enum
 * crosses the wire as its number. `state !== 'Closed'` was therefore `0 !== 'Closed'`, true forever,
 * and the outage banner declared the reader down on every page load while `capHeld`
 * (`state === 'Closed'`) could never be true, so the budget notice was unreachable.
 *
 * TypeScript could not catch it: the DTO is cast from `ApiResponse<VerifierStatus>` at the service
 * boundary, so the declared type was simply believed. Keep this an `enum`, like every other DTO enum
 * in this file, so the comparison is against a number and a typo is a compile error.
 */
export enum VerifierBreakerState {
  Closed = 0,
  Open = 1,
  HalfOpen = 2,
}

/**
 * 86d3yeh4j — `GET verifier-status`. Readable by anyone who can open the capture page, NOT gated on
 * `docscan.ai_manage`: the audience is whoever is standing at the scanner watching their scans stop
 * coming back, not the administrator who tunes the model map.
 */
export interface VerifierStatus {
  state: VerifierBreakerState;
  /** Scans parked because the reader is down — the "N scans are waiting" figure. */
  heldCount: number;
  /** Scans parked on an exhausted daily budget. A different fix, so a different number. */
  capHeldCount: number;
  since: string | null;
  /** Typed failure class (e.g. `Http401`), never a raw upstream message. */
  lastFailure: string | null;
  consecutiveFailures: number;
  nextProbeAt: string | null;
  lastClosedAt: string;
}

/**
 * What the calibration window has produced. Mirrors `DocVerificationStatsDto`.
 *
 * ⚠️ Two honesty constraints the UI must carry:
 * 1. Auto-linked scans are EXCLUDED from accuracy — for those, "correct" is whatever the gate chose,
 *    so scoring against them is circular. Hence the `humanResolved*` split.
 * 2. Human resolutions are themselves ~4.4% wrong on the calibration corpus, the same order as the
 *    effects being measured. Directions, not settled rates.
 */
export interface VerificationStats {
  totalAttempts: number;
  windowTarget: number;

  autoLinked: number;
  conflicts: number;
  aiSuggestions: number;
  unconfirmed: number;
  unreadable: number;
  verifierUnavailable: number;
  spotChecks: number;
  /**
   * 86d403p4v D6 — how many of `totalAttempts` were REPLACEMENT uploads rather than fresh intake
   * scans. Owner decision: replacements count toward the window, tagged so the two populations can be
   * told apart — on an intake scan the second reader discovers a number, on a replacement it only
   * confirms one that is already known, so pooling them silently would move the headline accuracy
   * with nothing on screen to explain why.
   *
   * Reads 0 for any window opened before this shipped: the replacement path wrote no attempt rows at
   * all, which is a large part of why the black hole went unseen.
   */
  replacementAttempts: number;

  humanResolvedTotal: number;
  decoderReadsWithTruth: number;
  decoderCorrect: number;
  aiReadsWithTruth: number;
  aiCorrect: number;

  spotChecksResolved: number;
  spotChecksConfirmed: number;

  callsToday: number;
  dailyCallCap: number;
  /**
   * 86d3ygnz3 — second readings the cap REFUSED today. Refusals, not distinct scans: a held scan is
   * re-armed whenever the budget looks like it has room and can be refused again. In-process on the
   * server, so it resets on the UTC day roll AND on an API restart.
   */
  deniedToday: number;
  autoLinkedToday: number;
  spotCheckRatePercent: number;
  verifierEnabled: boolean;
  level: string;
  promptVersion: string;
}

/** Per-item outcome of a bulk scan rejection (86d3x48q7). Deliberately not all-or-nothing. */
export interface BulkRejectResult {
  requested: number;
  rejected: number;
  /** Items that did NOT reject — they stay OPEN in the queue so they can be retried. */
  failed: { reviewItemId: number; label: string | null; message: string }[];
}

export interface ReviewIdentification {
  /** What the barcode decoder read, or null when it read nothing. */
  decoderRead: string | null;
  /** What the AI second reader read, or null when it declined. Never a partial or "corrected" number. */
  aiRead: string | null;

  decoderExists: boolean;
  /** File label this number is already attached to, or null when it is free. */
  decoderClaimedByFile: string | null;
  decoderEntityName: string | null;

  aiExists: boolean;
  aiClaimedByFile: string | null;
  aiEntityName: string | null;
}

// ---------------------------------------------------------------- NetSuite preview

export interface NsPreviewMatch {
  internalId: string;
  entityName: string | null;
  tranDate: string | null;
  amount: number | null;
  voided: boolean;
  currentCustbodyValue: string | null;
  alreadyHasValue: boolean;
}

export interface NsPreview {
  docNumber: string;
  prefix: string;
  nsRecordType: string;
  matches: NsPreviewMatch[];
  count: number;
}

// ---------------------------------------------------------------- upload + live stream

/** Answer to one uploaded scan. A duplicate carries the EXISTING document's current state. */
export interface ScanUploadResult {
  fileId: number;
  duplicate: boolean;
  requeued: boolean;
  processState: DocFileProcessState;
  docNumber: string | null;
  docStatus: DocScanStatus | null;
  linkStatus: DocLinkStatus | null;
}

/**
 * One phase change of one file's journey row. `phase` is a server string, not an enum —
 * the stream is a progress feed, and the row morphs IN PLACE keyed on fileId (or docNumber
 * for document-level events, which carry a null fileId).
 */
export type DocStreamPhase =
  | 'uploading'
  | 'reading'
  | 'captured'
  | 'linked'
  | 'waiting'
  | 'review'
  | 'duplicate'
  | 'failed'
  /**
   * 86d3x48q7 (T13) — the AI second reading is in flight. Sits BETWEEN `reading` and `waiting`:
   * verification is a step after decode, and the operator learns the pipeline's shape by watching
   * the row morph through it. See PHASE_RANK in lib/streamReducers.ts — the rank, not this union
   * order, is what stops a late event downgrading a row.
   */
  | 'verifying'
  /**
   * 86d3yeh4j — the second reading failed on something transient and is being retried. Carries
   * `attempt`/`of` so the row can say which try it is on: a row that has simply stopped moving is
   * indistinguishable from a broken one, and that is what sends people to ask whether it is stuck.
   */
  | 'retrying'
  /**
   * 86d3yeh4j — PARKED, because the second reader is down for everyone (or its daily budget is
   * spent). Explicitly NOT a failure state: nothing is lost, nothing reached the review queue, and
   * nobody needs to do anything. Ranked with the other in-flight phases (see PHASE_RANK) so a late
   * hold event can never downgrade a row that has since been captured.
   */
  | 'held'
  /** A deliberate viewer update/restore of an EXISTING document (T7, 86d3uf18j M4) — morphs the
   *  document's row in place; NOT a fresh capture, so it isn't counted in the session tally. */
  | 'updated';

export interface DocStreamEvent {
  fileId: number | null;
  phase: DocStreamPhase;
  docNumber: string | null;
  prefix: string | null;
  at: string;
  /**
   * Client-minted correlation id (86d3uf18j), echoed by the server on scan-level events. Lets the
   * browser match this event to its optimistic journey row even when the upload response was lost —
   * so a captured/linked doc never orphans a phantom "Failed" row. Null on document-level events
   * (link lane), which reconcile by docNumber.
   */
  clientUploadId: string | null;
  /**
   * 86d3yeh4j — which try this is, out of how many are configured. Present only on `retrying`; the
   * server omits them everywhere else, so treat absence as "not a retry" rather than as try 1.
   */
  attempt?: number | null;
  of?: number | null;
  /**
   * 86d3ygnz3 — why a `held` row is held: `'outage'` (the reader is down for everyone) or `'quota'`
   * (the daily budget is spent). One phase, two causes, and the operator's next move differs — wait
   * out a fix, or ask an admin to raise the cap. Absent on every other phase.
   */
  holdReason?: 'outage' | 'quota' | null;
}

// ---------------------------------------------------------------- today's intake (86d3yek7d)

/**
 * Mirrors `DocIntakeTodayDto`. The honest source behind the "Today" card, the tally strip and the
 * station pill — replacing a per-tab counter and a tally derived from the live feed's ~50-EVENT
 * replay window (which is why those numbers changed on every reload).
 *
 * ⚠️ These are the CURRENT STATE of the files uploaded today, not a running count of events. The
 * schema carries no attach/link/failure timestamps, so "captured today" in the event sense is not
 * buildable without a migration. A file uploaded today and linked tomorrow leaves today's `linked`
 * and joins tomorrow's — it belongs to its upload day's cohort, and that cohort's state keeps
 * moving. Say so in any copy that renders these.
 */
export interface IntakeToday {
  /** The Philippine calendar day these numbers cover, `yyyy-MM-dd`. Lets a long-lived tab notice midnight. */
  day: string;
  /** Files uploaded today across every station. A re-scan of bytes DOP already has creates no row. */
  scanned: number;
  captured: number;
  linked: number;
  /** Today's uploads that could not be read AND are still unattached — a resolved one is no longer counted. */
  failed: number;
  /** Today's uploads from the station the caller asked about; null when no station was named. */
  stationScanned: number | null;
}

// ---------------------------------------------------------------- file update lane (T7)

/**
 * Barcode verdict for a candidate replacement file, computed server-side BEFORE commit.
 * `barcodePage` is the BARCODE's page, never the file's page count.
 */
export type FilePreflightVerdict = 'match' | 'moved' | 'mismatch' | 'none' | 'multiple';

export type FileDuplicateScope =
  | 'current'
  | 'olderVersion'
  | 'pendingReview'
  | 'otherDocument'
  /** The bytes match an UNATTACHED scan sitting in the review queue (visible to this caller). */
  | 'unprocessedScan';

/** Where duplicate bytes already live. `docNumber` is withheld for families the caller can't view. */
export interface FileDuplicateInfo {
  scope: FileDuplicateScope;
  docNumber: string | null;
  version: number | null;
  /** Identifying context for `unprocessedScan` (no number exists): when it arrived + how big it is. */
  uploadedAt: string | null;
  pageCount: number | null;
}

export interface FilePreflightResult {
  pageCount: number;
  sizeBytes: number;
  sha256: string;
  barcodeNumber: string | null;
  barcodePage: number | null;
  matchesTarget: boolean;
  verdict: FilePreflightVerdict;
  /** Every distinct registry-valid number found (drives the 'multiple' copy). */
  numbers: string[];
  /** Set when these exact bytes already exist in DOP — a hard stop for commit. */
  duplicate: FileDuplicateInfo | null;
  /** Rendered page 1 of the candidate file (webp, base64) — the "New file" compare preview. */
  previewPage1Base64: string | null;
}

// ---------------------------------------------------------------- public share links (T8)

/** The active public link for a document + its access trail. `null` from the API = no active link. */
export interface ShareLink {
  url: string;
  createdAt: string;
  openCount: number;
  lastOpenedAt: string | null;
}

/** Everything the public customer page is allowed to know — nothing else is ever sent. */
export interface PublicDocument {
  docNumber: string;
  familyLabel: string;
  pageCount: number;
}

/**
 * What a signed-in, permitted caller gets back when resolving a capability token (86d3uu104, T10).
 * Anyone else gets the neutral 404 instead, so this shape never reaches an unpermitted client.
 */
export interface TokenResolve {
  docNumber: string;
  /**
   * The link has been replaced or revoked. Staff still resolve it — pasting the customer's "broken"
   * link is support's first move — but the viewer MUST say so, or a staffer sees it working and tells
   * the customer it's fine.
   */
  superseded: boolean;
  revokedAt: string | null;
}

// ---------------------------------------------------------------- registry query

export interface DocumentRegistryQuery {
  types?: string[];
  q?: string;
  /** PHILIPPINE calendar dates (YYYY-MM-DD). from == to is a single day; the server widens it. */
  scannedFrom?: string;
  scannedTo?: string;
  tranFrom?: string;
  tranTo?: string;
  linkStatus?: DocLinkStatus;
  status?: DocScanStatus;
  needsReview?: boolean;
  /**
   * 86d3x48q7 (T13) — how the number was arrived at. A FILTER, never a column: see
   * `config/verification.ts`. `'none'` is the pre-feature bucket and must stay reachable.
   */
  verification?: RegistryIdentificationFilter;
  page?: number;
  pageSize?: number;
}

/**
 * Registry identification filter values (86d3x48q7 T13). Declared HERE rather than in
 * `config/verification.ts` because that module imports from this one — putting it there and
 * referencing it back would make the two files import each other.
 *
 * `'none'` = no identification attempt exists at all. Every document captured before the second
 * reader shipped is in that bucket, so it is the honest answer to "what predates this feature?" and
 * must never be folded into the others.
 */
export type RegistryIdentificationFilter =
  | 'confirmed'
  | 'suggestion'
  | 'conflict'
  | 'unconfirmed'
  | 'unavailable'
  | 'none';

/**
 * The third status axis (86d3x48q7 T13) — how a number was arrived at, independent of document
 * status and link status. Labels, tones and the verbatim tooltips live in `config/verification.ts`;
 * only the union lives here, because that module imports from this one.
 *
 * ⚠️ `unreadable` (neither reader got a number) and `unavailable` (the check never ran) are
 * DIFFERENT states with different fixes, and the UI must never collapse them.
 */
export type DocVerificationState =
  | 'pending'
  | 'deferred'
  | 'confirmed'
  | 'conflict'
  | 'suggestion'
  | 'unconfirmed'
  | 'unreadable'
  | 'unavailable';

export interface ReviewQueueQuery {
  kind?: DocReviewKind;
  page?: number;
  pageSize?: number;
}
